import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BarChart, TrendingUp, Mail, Users, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CampaignReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchCampaigns();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/email/campaigns/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/email/campaigns/search`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      sent: 'bg-green-100 text-green-700',
      sending: 'bg-yellow-100 text-yellow-700',
      failed: 'bg-red-100 text-red-700',
      draft: 'bg-gray-100 text-gray-700'
    };
    return colors[status] || colors.draft;
  };

  const getCampaignIcon = (type) => {
    const icons = {
      'monthly_magazine': '📰',
      'expiry_30_days': '⏰',
      'expiry_15_days': '⚠️',
      'expiry_7_days': '🚨'
    };
    return icons[type] || '📧';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#7c3aed' }}>
              📊 Campaign Reports
            </h1>
          </div>
          <Button onClick={() => navigate('/send-campaign')} style={{ backgroundColor: '#7c3aed' }}>
            + Send New Campaign
          </Button>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="form-section">
              <div className="flex items-center space-x-3">
                <Mail className="text-purple-600" size={32} />
                <div>
                  <p className="text-sm text-gray-600">Emails Sent</p>
                  <p className="text-2xl font-bold text-purple-700">{stats.this_month.sent.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="flex items-center space-x-3">
                <CheckCircle className="text-green-600" size={32} />
                <div>
                  <p className="text-sm text-gray-600">Delivered</p>
                  <p className="text-2xl font-bold text-green-700">{stats.this_month.delivered.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">{stats.this_month.delivery_rate}%</p>
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="flex items-center space-x-3">
                <TrendingUp className="text-blue-600" size={32} />
                <div>
                  <p className="text-sm text-gray-600">Opened</p>
                  <p className="text-2xl font-bold text-blue-700">{stats.this_month.opened.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">{stats.this_month.open_rate}%</p>
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="flex items-center space-x-3">
                <XCircle className="text-red-600" size={32} />
                <div>
                  <p className="text-sm text-gray-600">Bounced</p>
                  <p className="text-2xl font-bold text-red-700">{stats.this_month.bounced.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Campaigns List */}
        <div className="form-section">
          <h2 className="text-2xl font-bold mb-6" style={{ color: '#7c3aed' }}>All Campaigns</h2>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading campaigns...</p>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-12">
              <BarChart size={64} className="mx-auto mb-4 text-gray-400" />
              <h3 className="text-xl font-bold mb-2">No Campaigns Yet</h3>
              <p className="text-gray-600 mb-6">Send your first email campaign to see reports here</p>
              <Button onClick={() => navigate('/send-campaign')} style={{ backgroundColor: '#7c3aed' }}>
                Send First Campaign
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {campaigns.map((campaign) => (
                <div key={campaign.id} className="border rounded-lg p-6 hover:shadow-lg transition-shadow bg-white">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-2xl">{getCampaignIcon(campaign.campaign_type)}</span>
                        <div>
                          <h3 className="text-lg font-bold">{campaign.campaign_name}</h3>
                          <p className="text-sm text-gray-600">{campaign.subject}</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">Sent: {formatDate(campaign.sent_at)}</p>

                      {/* Stats Row */}
                      <div className="grid grid-cols-5 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-gray-600">Total</p>
                          <p className="text-lg font-bold">{campaign.total_recipients}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Sent</p>
                          <p className="text-lg font-bold text-blue-600">{campaign.sent_count}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Delivered</p>
                          <p className="text-lg font-bold text-green-600">{campaign.delivered_count}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Opened</p>
                          <p className="text-lg font-bold text-purple-600">{campaign.opened_count}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Bounced</p>
                          <p className="text-lg font-bold text-red-600">{campaign.bounced_count}</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(campaign.status)}`}>
                        {campaign.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignReports;